import React, { useState } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ContactForm from '../components/ContactForm';
import { useTranslation } from '../hooks/useTranslation';

const { FiShield, FiClock, FiCheckCircle, FiArrowRight, FiStar, FiUser, FiLock, FiHome, FiFileText, FiHeart, FiActivity, FiX, FiDollarSign, FiMail, FiCalendar, FiUsers, FiAlertTriangle, FiBriefcase, FiTrendingUp } = FiIcons;

function OnlineVsAgent() {
  const [coverageAmount, setCoverageAmount] = useState(500000);
  const [termLength, setTermLength] = useState(20);
  const [age, setAge] = useState(35);
  const [isSmoker, setIsSmoker] = useState(false);
  const [healthCondition, setHealthCondition] = useState('average');
  const [showContactForm, setShowContactForm] = useState(false);
  const { t } = useTranslation();

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.2 } }
  };

  const handleGetQuote = () => {
    window.open('https://quote.burbankinsuranceservices.com/1b0DiB', '_blank');
  };

  const handleContactAgent = () => {
    setShowContactForm(true);
  };

  // Enhanced premium calculation with health conditions
  const calculatePremium = () => {
    let baseRate;
    
    if (age <= 25) baseRate = 0.08;
    else if (age <= 30) baseRate = 0.10;
    else if (age <= 35) baseRate = 0.13;
    else if (age <= 40) baseRate = 0.17;
    else if (age <= 45) baseRate = 0.25;
    else if (age <= 50) baseRate = 0.38;
    else if (age <= 55) baseRate = 0.58;
    else if (age <= 60) baseRate = 0.88;
    else baseRate = 1.35;

    const coverageInThousands = coverageAmount / 1000;
    const termMultiplier = termLength === 10 ? 0.85 : termLength === 20 ? 1 : termLength === 30 ? 1.2 : 1;
    const smokerMultiplier = isSmoker ? 2.5 : 1;
    const healthMultiplier = healthCondition === 'poor' ? 1.4 : healthCondition === 'average' ? 1.0 : healthCondition === 'great' ? 0.75 : 1.0;

    const monthlyPremium = baseRate * coverageInThousands * termMultiplier * smokerMultiplier * healthMultiplier;
    return Math.round(monthlyPremium);
  };

  const calculateSavings = () => {
    if (healthCondition === 'poor') return null;
    const currentPremium = calculatePremium();
    const poorHealthPremium = Math.round(calculatePremium() / (healthCondition === 'average' ? 1.0 : 0.75) * 1.4);
    const monthlySavings = poorHealthPremium - currentPremium;
    const annualSavings = monthlySavings * 12;
    return { monthly: monthlySavings, annual: annualSavings };
  };

  const healthOptions = t('onlineVsAgent.healthOptions');
  const onlineBenefits = t('onlineVsAgent.onlineBenefits');
  const agentBenefits = t('onlineVsAgent.agentBenefits');

  const onlineProfiles = [
    {
      icon: FiUser,
      title: "Young Professionals",
      description: "Healthy individuals under 50 with straightforward insurance needs and busy schedules.",
      characteristics: ["Generally healthy", "Standard coverage amounts", "Tech-savvy", "Values convenience"],
      ageRange: "Ages 25-45"
    },
    {
      icon: FiClock,
      title: "Time-Conscious Buyers", 
      description: "People who want insurance protection without the hassle of appointments and lengthy processes.",
      characteristics: ["Busy lifestyle", "Prefers self-service", "Researches online", "Values efficiency"],
      ageRange: "Any age"
    },
    {
      icon: FiDollarSign,
      title: "Cost-Conscious Families",
      description: "Budget-minded consumers looking for the best rates without agent markup.",
      characteristics: ["Price sensitive", "Straightforward needs", "Minimal health issues", "Direct buyers"],
      ageRange: "Ages 25-50"
    },
    {
      icon: FiHome,
      title: "Privacy Seekers",
      description: "Individuals who prefer to research and purchase insurance without sales pressure.",
      characteristics: ["Values privacy", "Self-directed", "Dislikes sales calls", "Independent decision makers"],
      ageRange: "Any age"
    }
  ];

  const agentProfiles = [
    {
      icon: FiAlertTriangle,
      title: "High-Risk Applicants",
      description: "People with serious health conditions, dangerous occupations, or risky hobbies.",
      characteristics: ["Chronic health conditions", "High-risk occupation", "Extreme sports", "Previous declines"],
      examples: ["Diabetes, heart disease", "Pilot, offshore worker", "Skydiving, racing", "Prior insurance denials"]
    },
    {
      icon: FiBriefcase,
      title: "High Net Worth Individuals", 
      description: "Wealthy clients needing large policies, estate planning, or business insurance.",
      characteristics: ["$1M+ coverage needs", "Estate planning", "Business insurance", "Tax strategies"],
      examples: ["Executive protection", "Key person insurance", "Buy-sell agreements", "Wealth transfer"]
    },
    {
      icon: FiTrendingUp,
      title: "Complex Financial Situations",
      description: "Clients with intricate financial needs requiring customized insurance solutions.",
      characteristics: ["Multiple policies", "Investment components", "Trust structures", "International exposure"],
      examples: ["Split-dollar plans", "COLI arrangements", "International coverage", "Policy optimization"]
    },
    {
      icon: FiUsers,
      title: "Guidance Seekers",
      description: "People who prefer personal consultation and hand-holding through the process.",
      characteristics: ["First-time buyers", "Overwhelmed by options", "Prefers human interaction", "Needs education"],
      examples: ["New parents", "Recent widows", "Retirement planning", "Family protection"]
    }
  ];

  const comparisonData = [
    { feature: "Application Time", online: "10-15 minutes", agent: "1-3 hours + meetings", winner: "online" },
    { feature: "Medical Exam", online: "Usually not required", agent: "Often required", winner: "online" },
    { feature: "Cost", online: "Lower (no commissions)", agent: "Higher (agent fees)", winner: "online" },
    { feature: "Availability", online: "24/7 online", agent: "Business hours only", winner: "online" },
    { feature: "Personal Guidance", online: "AI chatbot support", agent: "Face-to-face consultation", winner: "agent" },
    { feature: "Complex Cases", online: "Limited to standard risks", agent: "Handles all risk types", winner: "agent" },
    { feature: "Coverage Limits", online: "Up to $2M typically", agent: "No limits", winner: "agent" },
    { feature: "Ongoing Support", online: "Online customer service", agent: "Dedicated relationship manager", winner: "agent" }
  ];

  const trustLogos = [
    { src: "https://app1.sharemyimage.com/2025/08/18/cdnlogo.com_business-insider.png", alt: "Business Insider", className: "h-8" },
    { src: "https://app1.sharemyimage.com/2025/08/18/603-6035540_yahoo-finance-logo-png-transparent-png.png", alt: "Yahoo Finance", className: "h-8" },
    { src: "https://app1.sharemyimage.com/2025/08/18/FORTUNE-LOGO-2016.png", alt: "Fortune", className: "h-8" },
    { src: "https://app1.sharemyimage.com/2025/08/18/png-clipart-logo-forbes-industry-magazine-black-cover-company-text.png", alt: "Forbes", className: "h-8" }
  ];

  const savings = calculateSavings();

  return (
    <div className="min-h-screen">
      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex justify-between items-center p-6 border-b">
              <h3 className="text-2xl font-semibold">{t('onlineVsAgent.contactAgent')}</h3>
              <button
                onClick={() => setShowContactForm(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <SafeIcon icon={FiX} className="text-2xl" />
              </button>
            </div>
            <div className="p-6">
              <ContactForm onClose={() => setShowContactForm(false)} buttonText="Send Message" />
            </div>
          </motion.div>
        </div>
      )}

      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerChildren}
            className="text-center mb-16"
          >
            <motion.h1
              variants={fadeInUp}
              className="text-5xl md:text-6xl font-bold text-gray-900 mb-6"
            >
              {t('onlineVsAgent.heroTitle')} <span className="text-blue-600 block">{t('onlineVsAgent.heroSubtitle')}</span>
            </motion.h1>
            
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-600 max-w-3xl mx-auto mb-8"
            >
              {t('onlineVsAgent.heroDescription')}
            </motion.p>
            
            <motion.button
              variants={fadeInUp}
              onClick={handleGetQuote}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
            >
              <span>{t('onlineVsAgent.getOnlineQuote')}</span>
              <SafeIcon icon={FiArrowRight} />
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Comparison Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-4xl font-bold text-gray-900 text-center mb-16"
          >
            {t('onlineVsAgent.sideByySideComparison')}
          </motion.h2>

          <div className="grid lg:grid-cols-2 gap-8 mb-16">
            {/* Online Insurance */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-8 border-2 border-green-200"
            >
              <div className="text-center mb-8">
                <div className="bg-green-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiClock} className="text-white text-2xl" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">{t('onlineVsAgent.onlineInsurance')}</h3>
                <p className="text-green-700 font-medium">{t('onlineVsAgent.onlineSubtitle')}</p>
              </div>

              <div className="space-y-4 mb-8">
                {onlineBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiCheckCircle} className="text-green-600 text-xl mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                      <p className="text-gray-600 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleGetQuote}
                className="w-full bg-green-600 hover:bg-green-700 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                {t('onlineVsAgent.getOnlineQuoteBtn')}
              </button>
            </motion.div>

            {/* Agent Insurance */}
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeInUp}
              className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border-2 border-blue-200"
            >
              <div className="text-center mb-8">
                <div className="bg-blue-600 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiUsers} className="text-white text-2xl" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-2">{t('onlineVsAgent.agentInsurance')}</h3>
                <p className="text-blue-700 font-medium">{t('onlineVsAgent.agentSubtitle')}</p>
              </div>

              <div className="space-y-4 mb-8">
                {agentBenefits.map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <SafeIcon icon={FiUsers} className="text-blue-600 text-xl mt-1" />
                    <div>
                      <h4 className="font-semibold text-gray-900">{benefit.title}</h4>
                      <p className="text-gray-600 text-sm">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <button
                onClick={handleContactAgent}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-semibold transition-colors"
              >
                {t('onlineVsAgent.contactAgent')}
              </button>
            </motion.div>
          </div>

          {/* Detailed Comparison Table */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-gray-50 rounded-2xl p-8"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">{t('onlineVsAgent.featureComparison')}</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-4 px-4 font-semibold text-gray-900">Feature</th>
                    <th className="text-center py-4 px-4 font-semibold text-green-600">Online</th>
                    <th className="text-center py-4 px-4 font-semibold text-blue-600">Agent</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonData.map((item, index) => (
                    <tr key={index} className="border-b border-gray-200">
                      <td className="py-4 px-4 font-medium text-gray-900">{item.feature}</td>
                      <td className={`py-4 px-4 text-center ${item.winner === 'online' ? 'bg-green-50 font-semibold text-green-800' : 'text-gray-600'}`}>
                        {item.winner === 'online' && <SafeIcon icon={FiCheckCircle} className="inline mr-2 text-green-600" />}
                        {item.online}
                      </td>
                      <td className={`py-4 px-4 text-center ${item.winner === 'agent' ? 'bg-blue-50 font-semibold text-blue-800' : 'text-gray-600'}`}>
                        {item.winner === 'agent' && <SafeIcon icon={FiCheckCircle} className="inline mr-2 text-blue-600" />}
                        {item.agent}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Who Should Choose Online */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-4xl font-bold text-gray-900 text-center mb-16"
          >
            {t('onlineVsAgent.onlinePerfectFor')}
          </motion.h2>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {onlineProfiles.map((profile, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-white rounded-xl p-6 shadow-lg border-l-4 border-green-500 hover:shadow-xl transition-all duration-300"
              >
                <div className="text-center mb-4">
                  <div className="bg-green-100 rounded-full p-3 w-16 h-16 flex items-center justify-center mx-auto mb-3">
                    <SafeIcon icon={profile.icon} className="text-green-600 text-2xl" />
                  </div>
                  <h4 className="text-xl font-bold text-gray-900 mb-2">{profile.title}</h4>
                  <span className="text-sm text-green-600 font-medium">{profile.ageRange}</span>
                </div>
                <p className="text-gray-600 mb-4 text-sm">{profile.description}</p>
                <div className="space-y-2">
                  {profile.characteristics.map((char, charIndex) => (
                    <div key={charIndex} className="flex items-center space-x-2">
                      <SafeIcon icon={FiCheckCircle} className="text-green-500 text-sm" />
                      <span className="text-gray-600 text-sm">{char}</span>
                    </div>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <button
              onClick={handleGetQuote}
              className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
            >
              <span>{t('onlineVsAgent.getOnlineQuoteBtn')}</span>
              <SafeIcon icon={FiArrowRight} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* When You Need an Agent */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.h2
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-4xl font-bold text-gray-900 text-center mb-16"
          >
            {t('onlineVsAgent.whenNeedAgent')}
          </motion.h2>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
            className="grid md:grid-cols-2 gap-8"
          >
            {agentProfiles.map((profile, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="bg-gradient-to-br from-red-50 to-orange-50 rounded-xl p-6 shadow-lg border-l-4 border-red-500"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-red-100 rounded-full p-3 flex-shrink-0">
                    <SafeIcon icon={profile.icon} className="text-red-600 text-2xl" />
                  </div>
                  <div className="flex-1">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{profile.title}</h4>
                    <p className="text-gray-600 mb-4">{profile.description}</p>
                    <div className="mb-4">
                      <h5 className="font-semibold text-gray-800 mb-2">Characteristics:</h5>
                      <div className="grid grid-cols-1 gap-1">
                        {profile.characteristics.map((char, charIndex) => (
                          <div key={charIndex} className="flex items-center space-x-2">
                            <SafeIcon icon={FiAlertTriangle} className="text-red-500 text-sm" />
                            <span className="text-gray-600 text-sm">{char}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div>
                      <h5 className="font-semibold text-gray-800 mb-2">Examples:</h5>
                      <div className="grid grid-cols-1 gap-1">
                        {profile.examples.map((example, exampleIndex) => (
                          <div key={exampleIndex} className="flex items-center space-x-2">
                            <SafeIcon icon={FiCheckCircle} className="text-blue-500 text-sm" />
                            <span className="text-gray-600 text-sm">{example}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 mb-6 max-w-2xl mx-auto">
              <h3 className="text-xl font-bold text-red-800 mb-3">{t('onlineVsAgent.importantNote')}</h3>
              <p className="text-red-700">
                {t('onlineVsAgent.complexCasesNote')}
              </p>
            </div>
            <button
              onClick={handleContactAgent}
              className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 mx-auto"
            >
              <SafeIcon icon={FiMail} />
              <span>{t('onlineVsAgent.contactOurAgents')}</span>
            </button>
          </motion.div>
        </div>
      </section>

      {/* Interactive Premium Calculator */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">{t('onlineVsAgent.seeEstimatedPremium')}</h2>
            <p className="text-xl text-gray-600">{t('onlineVsAgent.adjustControls')}</p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="bg-white rounded-2xl p-8 shadow-lg"
          >
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Controls Section */}
              <div className="lg:col-span-2 space-y-8">
                {/* Coverage Amount */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    {t('onlineVsAgent.coverageAmount')}: ${coverageAmount.toLocaleString()}
                  </label>
                  <div className="relative">
                    <input
                      type="range"
                      min="100000"
                      max="2000000"
                      step="50000"
                      value={coverageAmount}
                      onChange={(e) => setCoverageAmount(parseInt(e.target.value))}
                      className="w-full h-3 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span>$100K</span>
                    <span>$500K</span>
                    <span>$1M</span>
                    <span>$2M</span>
                  </div>
                </div>

                {/* Age */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    {t('onlineVsAgent.age')}: {age} years old
                  </label>
                  <div className="relative">
                    <input
                      type="range"
                      min="18"
                      max="65"
                      step="1"
                      value={age}
                      onChange={(e) => setAge(parseInt(e.target.value))}
                      className="w-full h-3 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span>18</span>
                    <span>30</span>
                    <span>45</span>
                    <span>65</span>
                  </div>
                </div>

                {/* Term Length */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-3">
                    {t('onlineVsAgent.termLength')}: {termLength} years
                  </label>
                  <div className="relative">
                    <input
                      type="range"
                      min="10"
                      max="30"
                      step="10"
                      value={termLength}
                      onChange={(e) => setTermLength(parseInt(e.target.value))}
                      className="w-full h-3 bg-blue-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                  <div className="flex justify-between text-sm text-gray-500 mt-2">
                    <span>10 years</span>
                    <span>20 years</span>
                    <span>30 years</span>
                  </div>
                </div>

                {/* Smoker Status */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-4">
                    {t('onlineVsAgent.smokingStatus')}
                  </label>
                  <div className="flex space-x-4">
                    <button
                      onClick={() => setIsSmoker(false)}
                      className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${!isSmoker ? 'bg-green-600 text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      {t('onlineVsAgent.nonSmoker')}
                    </button>
                    <button
                      onClick={() => setIsSmoker(true)}
                      className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all duration-200 ${isSmoker ? 'bg-red-600 text-white shadow-lg' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
                    >
                      {t('onlineVsAgent.smoker')}
                    </button>
                  </div>
                  {isSmoker && (
                    <p className="text-sm text-red-600 mt-2">
                      {t('onlineVsAgent.smokingWarning')}
                    </p>
                  )}
                </div>

                {/* Health Condition */}
                <div>
                  <label className="block text-lg font-semibold text-gray-900 mb-4">
                    {t('onlineVsAgent.healthCondition')}
                  </label>
                  <div className="space-y-3">
                    {healthOptions.map((option, index) => (
                      <button
                        key={index}
                        onClick={() => setHealthCondition(['poor', 'average', 'great'][index])}
                        className={`w-full p-4 rounded-lg border-2 text-left transition-all duration-200 ${healthCondition === ['poor', 'average', 'great'][index] ? 'border-blue-500 bg-blue-50 shadow-lg' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`p-2 rounded-full ${healthCondition === ['poor', 'average', 'great'][index] ? 'bg-blue-600' : 'bg-gray-400'}`}>
                            <SafeIcon icon={index === 0 ? FiX : index === 1 ? FiActivity : FiHeart} className="text-white text-lg" />
                          </div>
                          <div className="flex-1">
                            <h4 className={`font-semibold ${healthCondition === ['poor', 'average', 'great'][index] ? 'text-blue-800' : 'text-gray-900'}`}>
                              {option.label}
                            </h4>
                            <p className={`text-sm mt-1 ${healthCondition === ['poor', 'average', 'great'][index] ? 'text-blue-700' : 'text-gray-600'}`}>
                              {option.description}
                            </p>
                            <div className={`text-xs mt-2 ${healthCondition === ['poor', 'average', 'great'][index] ? 'text-blue-600' : 'text-gray-500'}`}>
                              Examples: {option.examples.join(', ')}
                            </div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Results Section */}
              <div className="flex flex-col justify-center items-center bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl p-6">
                <div className="text-center mb-6">
                  <div className="text-6xl font-bold text-blue-600 mb-2">
                    ${calculatePremium()}
                  </div>
                  <div className="text-gray-600 text-lg">{t('onlineVsAgent.perMonth')}</div>
                  <div className="text-sm text-gray-500 mt-2">
                    ${(calculatePremium() * 12).toLocaleString()} {t('onlineVsAgent.perYear')}
                  </div>
                </div>

                {/* Health Savings Display */}
                {savings && (
                  <div className="bg-green-100 border border-green-200 rounded-lg p-4 mb-6 w-full">
                    <div className="text-center">
                      <div className="text-green-800 font-semibold mb-2">
                        💰 {t('onlineVsAgent.healthSavings')}
                      </div>
                      <div className="text-sm text-green-700">
                        {t('onlineVsAgent.youSave')} <span className="font-bold">${savings.monthly}/month</span> {t('onlineVsAgent.compared')}
                      </div>
                      <div className="text-xs text-green-600 mt-1">
                        {t('onlineVsAgent.thatsPerYear')} <span className="font-bold">${savings.annual.toLocaleString()}/year</span> {t('onlineVsAgent.inSavings')}
                      </div>
                    </div>
                  </div>
                )}

                <div className="bg-white rounded-lg p-4 mb-6 w-full text-sm">
                  <h4 className="font-semibold text-gray-900 mb-3">{t('onlineVsAgent.quoteDetails')}</h4>
                  <div className="space-y-1 text-gray-600">
                    <div className="flex justify-between">
                      <span>{t('onlineVsAgent.age')}:</span>
                      <span>{age} years old</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('onlineVsAgent.smoking')}:</span>
                      <span className={isSmoker ? 'text-red-600' : 'text-green-600'}>
                        {isSmoker ? t('onlineVsAgent.smoker') : t('onlineVsAgent.nonSmoker')}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('onlineVsAgent.health')}:</span>
                      <span className={healthCondition === 'poor' ? 'text-red-600' : healthCondition === 'average' ? 'text-yellow-600' : 'text-green-600'}>
                        {healthOptions.find((h, i) => ['poor', 'average', 'great'][i] === healthCondition)?.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('onlineVsAgent.coverage')}:</span>
                      <span>${coverageAmount.toLocaleString()}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('onlineVsAgent.term')}:</span>
                      <span>{termLength} years</span>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleGetQuote}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center space-x-2 w-full justify-center"
                >
                  <span>{t('onlineVsAgent.getExactQuote')}</span>
                  <SafeIcon icon={FiArrowRight} />
                </button>

                <p className="text-xs text-gray-500 mt-4 text-center">
                  {t('onlineVsAgent.estimateDisclaimer')}
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured In Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8">{t('onlineVsAgent.trustedByPublications')}</h3>
            <div className="flex flex-wrap items-center justify-center gap-8 opacity-60">
              {trustLogos.map((logo, index) => (
                <img
                  key={index}
                  src={logo.src}
                  alt={logo.alt}
                  className={`${logo.className} object-contain grayscale hover:grayscale-0 transition-all duration-300`}
                />
              ))}
            </div>
            <div className="mt-8">
              <img
                src="https://app1.sharemyimage.com/2025/08/18/ethos-logo_RGB_cypress.jpg"
                alt="Ethos Life Insurance"
                className="h-12 mx-auto"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Decision Helper CTA */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerChildren}
          >
            <motion.h2
              variants={fadeInUp}
              className="text-4xl font-bold text-white mb-6"
            >
              {t('onlineVsAgent.readyToChoose')}
            </motion.h2>
            
            <motion.p
              variants={fadeInUp}
              className="text-xl text-gray-300 mb-8 max-w-3xl mx-auto"
            >
              {t('onlineVsAgent.mostPeopleQualify')}
            </motion.p>
            
            <motion.div
              variants={fadeInUp}
              className="flex flex-col sm:flex-row gap-4 justify-center"
            >
              <button
                onClick={handleGetQuote}
                className="bg-green-600 hover:bg-green-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 text-lg"
              >
                <SafeIcon icon={FiClock} />
                <span>{t('onlineVsAgent.tryOnlineFirst')}</span>
              </button>
              <button
                onClick={handleContactAgent}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-200 flex items-center justify-center space-x-2 text-lg"
              >
                <SafeIcon icon={FiMail} />
                <span>{t('onlineVsAgent.contactAgentBtn')}</span>
              </button>
            </motion.div>
            
            <motion.div
              variants={fadeInUp}
              className="mt-8 text-gray-400"
            >
              <p className="mb-2">{t('onlineVsAgent.onlineApplication')}</p>
              <p>{t('onlineVsAgent.agentConsultation')}</p>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}

export default OnlineVsAgent;